import { useMemo } from "react";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import ListItem from "@/components/ListItem/ListItem";
import ObjectHeading from "@/components/ObjectHeading";
import SubSectionHeading from "@/components/SubSectionHeading";
import CommentList from "@/components/CommentList";
import useCapabilities from "@/hooks/useCapabilities";
import { formatIsoDatesInText, toFormat } from "@/lib/dateHelpers";
import { renderContent } from "@/lib/htmlContentHelpers";
import mapTaskComments from "@/pages/tasks/_utils/mapTaskComments";
import { getStageTag, getStageSplitAction } from "@/pages/tasks/_utils/taskHelpers";
import useTaskStageActions from "@/pages/tasks/_hooks/useTaskStageActions";
import useUpdateTask from "@/pages/tasks/_hooks/useUpdateTask";
import type { Action } from "@/components/MenuButton";
import { TaskStageValues, type CRMTask } from "@/types";
import { useNavigate } from "react-router-dom";
import {
  getReferenceIcon,
  getReferenceName,
  getReferenceSupportingText,
  getReferenceModifiedDate,
  getReferenceParentLink
} from "../ReferencesTable/types";

interface TaskSideSheetContentProps {
  task: CRMTask;
  onViewTask?: () => void;
  onAddReference?: () => void;
}

export default function TaskSideSheetContent({ task, onViewTask, onAddReference }: TaskSideSheetContentProps) {
  const navigate = useNavigate();
  const teamNames = (task.teamTags ?? []).map((tag) => tag.name).filter(Boolean);
  const teamsText = teamNames.join(", ");

  const commentData = useMemo(() => mapTaskComments(task.comments), [task.comments]);

  const stageTag = getStageTag(task);
  const formattedDueDate = task.dueDate ? toFormat(task.dueDate, "MMM dd, yyyy") : null;
  const actions = useTaskStageActions(task.id, { task, source: "task_side_sheet" });
  const { primary: primaryStageAction, alternatives: stageAlternatives } = getStageSplitAction(task.stage, actions);
  const {
    updateAssignee,
    loading: assigneeLoading,
    error: assigneeError
  } = useUpdateTask(task.id, {
    task,
    source: "task_side_sheet"
  });

  const { id: currentUserId } = useCapabilities();
  const isAssignedToMe = currentUserId != null && task.assignedTo?.id === currentUserId;

  const flaggedAlternatives: Action[] =
    stageAlternatives.length > 0
      ? [
          { ...stageAlternatives[0], section: undefined, dividerAbove: true },
          ...stageAlternatives.slice(1).map((action) => ({ ...action, section: undefined }))
        ]
      : [];

  const canAddReference = task.stage === TaskStageValues.OPEN && !!onAddReference;

  const headerActions: Action[] = [
    ...(primaryStageAction ? [{ ...primaryStageAction, iconName: undefined, variant: "outlined" as const }] : []),
    ...(onViewTask ? [{ text: "View Task", variant: "text" as const, onClick: onViewTask }] : []),
    ...(canAddReference
      ? [
          {
            text: "Add Reference",
            variant: "text" as const,
            onClick: onAddReference,
            testId: "add-reference-action",
            section: "Actions" as const
          }
        ]
      : []),
    ...flaggedAlternatives
  ];

  const combinedError = actions.error || assigneeError;

  return (
    <div>
      {combinedError && <Banner variant="error" description={combinedError} inline hideSecondaryDismiss />}
      <ObjectHeading
        iconName="inventory"
        title={task.name || "Untitled Task"}
        variant="condensed"
        tag={stageTag}
        actions={headerActions.length > 0 ? headerActions : undefined}
      />

      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Overview</SubSectionHeading>
        <div className="space-y-4 w-full">
          <div className="flex items-center justify-between">
            <ListItem
              useAvatar
              picture={(task.assignedTo ?? task.author)?.photo ?? undefined}
              overline={task.assignedTo ? "Assignee" : "Author"}
              headline={(task.assignedTo ?? task.author)?.name || null}
              supportingText={(task.assignedTo ?? task.author)?.email || null}
            />
            {!isAssignedToMe && currentUserId && (
              <Button variant="text" onClick={() => updateAssignee(currentUserId)} disabled={assigneeLoading}>
                Assign to Me
              </Button>
            )}
          </div>
          {teamsText ? (
            <ListItem iconName="assignment_ind" iconBackground overline="Teams" headline={teamsText} />
          ) : null}
          <ListItem iconName="event" iconBackground overline="Due Date" headline={formattedDueDate} />
        </div>
      </div>

      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Details</SubSectionHeading>
        <div className="space-y-4 w-full">
          <ListItem
            iconName="article"
            iconBackground
            overline="Description"
            headline={
              task.description ? (
                <div className="[&_p]:[font:inherit] [&_li]:[font:inherit]">
                  {renderContent(formatIsoDatesInText(task.description))}
                </div>
              ) : null
            }
            longContent={!!task.description}
          />
        </div>
      </div>

      {task.references && task.references.length > 0 && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Reference Information</SubSectionHeading>
          <div className="space-y-4 w-full">
            {task.references.map((ref) => {
              const parentLink = getReferenceParentLink(ref);

              return (
                <ListItem
                  key={ref.id}
                  compact
                  iconName={getReferenceIcon(ref.__typename)}
                  useAvatar
                  headline={getReferenceName(ref) || "--"}
                  supportingText={getReferenceSupportingText({
                    ...ref,
                    date: getReferenceModifiedDate(ref)
                  })}
                  action={parentLink ? () => navigate(parentLink) : undefined}
                />
              );
            })}
          </div>
        </div>
      )}

      {commentData.length > 0 && (
        <div className="mt-8 w-full">
          <CommentList comments={commentData} heading="Comments" headingVariant="subSection" compact />
        </div>
      )}
    </div>
  );
}
